import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import PhonePeService from '../services/phonepeApi';

export default function PaymentStatus() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [transactionDetails, setTransactionDetails] = useState(null);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      try {
        // Get transaction ID from URL parameters
        const transactionId = searchParams.get('transactionId');
        const merchantTransactionId = searchParams.get('merchantTransactionId');

        if (!merchantTransactionId) {
          setStatus('error');
          setMessage('Transaction ID not found');
          return;
        }

        // Check payment status with PhonePe
        const result = await PhonePeService.checkPaymentStatus(merchantTransactionId);

        if (result.success || result.code === 'PAYMENT_SUCCESS') {
          setStatus('success');
          setMessage('Payment completed successfully!');
          setTransactionDetails({
            transactionId: result.transactionId || transactionId,
            merchantTransactionId: result.merchantTransactionId,
            amount: result.amount ? `₹₹{result.amount / 100}` : 'N/A',
            planName: 'Premium Plan' // You might want to store this in localStorage or pass via URL
          });
        } else {
          setStatus('failed');
          setMessage(result.message || 'Payment failed or is pending');
        }

      } catch (error) {
        console.error('Payment status check error:', error);
        setStatus('error');
        setMessage('Failed to verify payment status. Please contact support.');
      }
    };

    checkPaymentStatus();
  }, [searchParams]);

  const handleContinue = () => {
    navigate('/dashboard');
  };

  const handleRetry = () => {
    navigate('/premium');
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto text-center space-y-8">
        {/* Header */}
        <div className="space-y-4">
          <h1 className="text-3xl font-bold text-white">Payment Status</h1>
          <p className="text-slate-400">Verifying your payment with PhonePe</p>
        </div>

        {/* Status Card */}
        <div className="bg-white/5 backdrop-blur-lg rounded-2xl border border-white/10 p-8">
          {status === 'loading' && (
            <div className="space-y-6">
              <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-fuchsia-500 mx-auto"></div>
              <div>
                <h2 className="text-xl font-semibold text-white mb-2">Verifying Payment...</h2>
                <p className="text-slate-400">Please wait while we confirm your payment status</p>
              </div>
            </div>
          )}

          {status === 'success' && (
            <div className="space-y-6">
              <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-green-400 mb-2">Payment Successful!</h2>
                <p className="text-slate-300 mb-4">{message}</p>

                {transactionDetails && (
                  <div className="bg-white/5 rounded-lg p-4 text-left">
                    <h3 className="font-semibold text-white mb-2">Transaction Details</h3>
                    <div className="space-y-1 text-sm text-slate-300">
                      <div>Transaction ID: {transactionDetails.transactionId}</div>
                      <div>Amount: {transactionDetails.amount}</div>
                      <div>Plan: {transactionDetails.planName}</div>
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={handleContinue}
                className="w-full px-6 py-3 bg-gradient-to-r from-fuchsia-600 to-purple-600 text-white rounded-lg hover:from-fuchsia-700 hover:to-purple-700 transition font-semibold"
              >
                Continue to Dashboard
              </button>
            </div>
          )}

          {status === 'failed' && (
            <div className="space-y-6">
              <div className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-red-400 mb-2">Payment Failed</h2>
                <p className="text-slate-300 mb-4">{message}</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleRetry}
                  className="flex-1 px-6 py-3 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition"
                >
                  Try Again
                </button>
                <button
                  onClick={handleContinue}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-fuchsia-600 to-purple-600 text-white rounded-lg hover:from-fuchsia-700 hover:to-purple-700 transition"
                >
                  Back to Premium
                </button>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-6">
              <div className="w-20 h-20 bg-yellow-500 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-yellow-400 mb-2">Verification Error</h2>
                <p className="text-slate-300 mb-4">{message}</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleRetry}
                  className="flex-1 px-6 py-3 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition"
                >
                  Try Again
                </button>
                <button
                  onClick={() => navigate('/premium')}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-fuchsia-600 to-purple-600 text-white rounded-lg hover:from-fuchsia-700 hover:to-purple-700 transition"
                >
                  Contact Support
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Help Text */}
        <div className="text-sm text-slate-400">
          <p>Having trouble? Contact our support team for assistance.</p>
        </div>
      </div>
    </Layout>
  );
}